import { createElement, useCallback, useEffect, useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query'
import { AlertTriangle, Loader2, Sparkles } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { BillingIntervalToggle } from '../components/BillingIntervalToggle'
import { CurrentPlanCard } from '../components/CurrentPlanCard'
import { UpgradePlanCard } from '../components/UpgradePlanCard'
import {
  formatCompactBytes,
  maxAnnualSavingsPercent,
  purchasablePlans,
} from '../utils/planDisplay'
import type { PlanChangePreflightResponse, PlanCode } from '@/lib/types/subscription'
import { useAuth } from '@/lib/auth/useAuth'
import { usePageLayout } from '@/hooks/usePageLayout'
import { Button } from '@/components/ui/button'
import SettingsSidebar from '@/components/app/sidebars/settings'
import { SettingsPage, SettingsSection } from '@/components/app/settings'
import { PlanChangeBlockedModal } from '@/components/app/subscription/PlanChangeBlockedModal'
import { UsageMetrics } from '@/components/app/subscription/UsageMetrics'
import {
  useCurrentUsage,
  usePlans,
} from '@/lib/db/rxdb/hooks/entities/subscriptions'
import {
  BILLING_INTERVALS,
  PLAN_CODES,
  SUBSCRIPTION_STATUS,
  isPaidPlanCode,
} from '@/lib/types/subscription'
import {
  getTrialDaysLeft,
  isSubscriptionActive,
  isTrialing as isSubscriptionTrialing,
} from '@/lib/types/subscriptionHelpers'
import { useSubscriptionContext } from '@/context/SubscriptionContext'
import {
  cancelSubscription,
  createBillingPortalSession,
  createCheckoutSession,
  planChangePreflight,
  reactivateSubscription,
  startTrial,
  upgradeSubscription,
} from '@/lib/api/features/subscriptions'
import { BillingProvider, useBilling } from '@/lib/billing/BillingProvider'
import { getCurrentSubscription } from '@/lib/api/features/subscriptions/api/getCurrentSubscription'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags'
import { useIsApiDown } from '@/lib/api/useIsApiDown'

/** `error.message` when present, else the fallback. */
function errorMessage(error: unknown, fallback: string): string {
  const message = (error as { message?: unknown } | null)?.message
  return typeof message === 'string' && message ? message : fallback
}

export default function SubscriptionSettingsPageWrapped() {
  // The app root doesn't wrap children in a QueryClientProvider (data loading
  // elsewhere is RxDB-driven), so this page scopes its own client. useState
  // keeps the same instance across re-renders without leaking across routes.
  // Billing is server-authoritative and online-only, so the current
  // subscription loads from the API (staleTime: 0) rather than the offline
  // RxDB cache — that read is what left a stale plan on screen after checkout.
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { staleTime: 0, retry: 1 },
          mutations: { retry: 0 },
        },
      }),
  )

  // Wrap the page in BillingProvider so /api/v1/billing/config is fetched
  // exactly when the user navigates here (not on app boot for users who
  // never visit billing). The inner component owns all the page logic.
  // QueryClientProvider sits outside BillingProvider so the inner component's
  // useQuery has a client regardless of billing-config load state.
  return (
    <QueryClientProvider client={queryClient}>
      <BillingProvider>
        <SubscriptionSettingsPageInner />
      </BillingProvider>
    </QueryClientProvider>
  )
}

function SubscriptionSettingsPageInner() {
  const { t } = useTranslation('settings')
  usePageLayout({
    sidebarContent: createElement(SettingsSidebar),
    showHeader: false,
  })

  // Current subscription is server-authoritative and online-only — load it
  // from the API instead of the offline RxDB cache so the plan reflects the
  // real billing state immediately after checkout (no stale/lagging read).
  // The plan *catalog* below still comes from RxDB (reference data, fine
  // offline).
  const {
    data: currentSubscription,
    isLoading: isSubscriptionLoading,
    isError: isSubscriptionError,
    refetch: refetchSubscription,
  } = useQuery({
    queryKey: ['subscription', 'current'],
    queryFn: getCurrentSubscription,
    staleTime: 0,
  })
  const subscription = currentSubscription?.subscription ?? null
  const plan = currentSubscription?.plan ?? null
  const isTrialing = isSubscriptionTrialing(subscription)
  const trialDaysLeft = getTrialDaysLeft(subscription)
  const isActive = isSubscriptionActive(subscription)
  const plans = usePlans()
  const auth = useAuth()
  // Shared across all plan cards so one "Billed yearly" switch flips every card
  // together (Linear-style). Default to yearly to lead with the best price.
  const [billingInterval, setBillingInterval] = useState<string>(
    BILLING_INTERVALS.ANNUAL,
  )
  const { trialEligibility, refreshTrialEligibility } =
    useSubscriptionContext()
  const billing = useBilling()
  // Provider for the *current row* — falls back to the env-default
  // billingProvider context (covers brand-new workspaces with no row yet).
  const effectiveBillingProvider =
    subscription?.billingProvider ?? billing.providerName ?? 'stripe'
  // Switching getter for the active provider id, mirroring the backend
  // routing rule. NOT a null-coalesce — both ids being populated would
  // otherwise route a Stripe row to Paddle.
  const providerSubscriptionId =
    effectiveBillingProvider === 'paddle'
      ? subscription?.paddleSubscriptionId ?? null
      : subscription?.stripeSubscriptionId ?? null
  const [isLoading, setIsLoading] = useState(false)
  // Post-checkout "settling" window. Billing is eventually consistent — after
  // the user returns from checkout (?success=true) the provider webhook updates
  // the backend asynchronously, so for a few seconds the plan hasn't flipped
  // yet. We show a "Finalizing…" state and poll until it converges (or times
  // out) instead of leaving the stale plan on screen.
  const [isFinalizing, setIsFinalizing] = useState(false)
  // Synchronous re-entry guard: blocks rapid double-clicks on a plan CTA before
  // the checkout session / Paddle overlay has a chance to open (state updates
  // are async, so a ref is needed to reject the second click in the same tick).
  const isSelectingPlanRef = useRef(false)
  const isApiDown = useIsApiDown()
  const offlineTitle = isApiDown ? t('workspaceSettings.offlineTitle') : undefined
  const usage = useCurrentUsage()

  // Phase 0: blocked-modal state shared by every plan-change entry point.
  const [blockedModal, setBlockedModal] = useState<{
    open: boolean
    targetPlan: PlanCode | null
    preflight: PlanChangePreflightResponse | null
  }>({ open: false, targetPlan: null, preflight: null })

  // Billing is eventually consistent: after checkout the provider webhook
  // settles the backend asynchronously, so a single refetch can still return
  // the pre-checkout plan. Refetch every 1500ms (up to 15000ms) until the
  // API reports a settled state — a started trial, or an active row on a
  // non-Free plan (Free is also "active", so status alone isn't enough for
  // paid). Resolves true on convergence, false on timeout. The caller owns
  // the `isFinalizing` overlay shown for the duration.
  const refetchUntilSettled = useCallback(async (): Promise<boolean> => {
    const deadline = Date.now() + 15000
    while (Date.now() < deadline) {
      const { data } = await refetchSubscription()
      const status = data?.subscription.status
      if (
        status === SUBSCRIPTION_STATUS.TRIALING ||
        (status === SUBSCRIPTION_STATUS.ACTIVE && data?.plan?.code !== 'free')
      ) {
        return true
      }
      if (Date.now() + 1500 >= deadline) {
        break
      }
      await new Promise((resolve) => setTimeout(resolve, 1500))
    }
    return false
  }, [refetchSubscription])

  // On returning from checkout (?success=true), show a finalizing state and
  // poll until the subscription reflects the purchased plan, then strip the
  // param. Covers the Stripe redirect and any flow that reloads the page; the
  // Paddle inline path drives the same indicator from handleSelectPlan below.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('success') !== 'true') {
      return
    }

    let cancelled = false
    setIsFinalizing(true)

    void (async () => {
      const converged = await refetchUntilSettled()
      if (cancelled) return
      setIsFinalizing(false)
      // Drop ?success=true so a refresh doesn't re-enter the finalizing state.
      const url = new URL(window.location.href)
      url.searchParams.delete('success')
      window.history.replaceState({}, '', url.toString())
      if (!converged) {
        toast.info(t('subscription.toast.finalizingDelayed'))
      }
    })()

    return () => {
      cancelled = true
    }
    // Run once on mount; success param is read imperatively.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleStartTrial = async () => {
    // Try to get email from auth context first
    let email = auth.user?.email ?? undefined

    // Fallback: prompt user to enter email if not available
    if (!email) {
      const promptEmail = prompt(t('subscription.prompt.billingEmail'))
      if (!promptEmail) {
        toast.error(t('subscription.toast.emailRequiredTrial'))
        return
      }
      email = promptEmail
    }

    setIsLoading(true)
    try {
      await startTrial({ billingEmail: email })
      toast.success(t('subscription.toast.trialStarted'))
      void refetchSubscription()
    } catch (error: unknown) {
      toast.error(errorMessage(error, t('subscription.toast.trialFailed')))
      logger.logError(
        error as Error,
        LogCategory.SUBSCRIPTION_TRIAL,
        {
          component: 'SubscriptionSettingsPage',
          action: 'start_trial',
        }
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleSelectPlan = async (planId: string, billingInterval: string) => {
    const selectedPlan = plans.find((p) => p.id === planId)
    if (!selectedPlan || selectedPlan.id === subscription?.planId) {
      return
    }

    // Block rapid re-clicks: the checkout session + Paddle overlay take a
    // moment to open, and without this each extra click spawns another
    // checkout. Disable via isLoading too, but the ref rejects clicks that
    // land before the re-render disables the button.
    if (isSelectingPlanRef.current) {
      return
    }
    isSelectingPlanRef.current = true
    setIsLoading(true)

    // Phase 0: run plan-change preflight before any Stripe call. The same
    // contract serves both upgrade-via-checkout and change-plan flows; if the
    // user can't switch (e.g. Individual requires Personal workspace + 1
    // member + 0 guests) the modal renders the structured violations and we
    // bail without touching Stripe.
    try {
      const preflight = await planChangePreflight(selectedPlan.code)
      if (preflight.blocked) {
        setBlockedModal({
          open: true,
          targetPlan: selectedPlan.code as PlanCode,
          preflight,
        })
        isSelectingPlanRef.current = false
        setIsLoading(false)
        return
      }
    } catch (error) {
      // Don't hard-fail on preflight error — backend will still enforce the
      // invariant on the actual checkout/change-plan call. Log and proceed.
      logger.logError(error as Error, LogCategory.SUBSCRIPTION, {
        component: 'SubscriptionSettingsPage',
        action: 'plan_change_preflight',
        planId,
      })
    }

    // Get email for billing
    let email = auth.user?.email ?? undefined

    if (!email) {
      const promptEmail = prompt(t('subscription.prompt.billingEmail'))
      if (!promptEmail) {
        toast.error(t('subscription.toast.emailRequiredCheckout'))
        isSelectingPlanRef.current = false
        setIsLoading(false)
        return
      }
      email = promptEmail
    }

    try {
      if (providerSubscriptionId) {
        if (selectedPlan.priceMonthly <= 0) {
          await cancelSubscription({
            cancelAtPeriodEnd: true,
            reason: 'User requested downgrade to free plan',
          })
          toast.success(t('subscription.toast.cancelScheduled'))
          // Immediate backend state change — a single refetch reflects the
          // scheduled cancellation (cancelAtPeriodEnd) without a settle loop.
          void refetchSubscription()
          return
        }

        await upgradeSubscription({
          planId,
          billingInterval: billingInterval as 'monthly' | 'annual',
        })
        toast.success(t('subscription.toast.upgradeSuccess'))
        // Refresh the current subscription + REST-only trialEligibility once
        // the change settles. Upgrade applies immediately, so a single refetch
        // is enough — no long settle loop needed.
        void refetchSubscription()
        void refreshTrialEligibility()
        return
      }

      if (!billing.client) {
        toast.error(t('subscription.toast.checkoutFailed'))
        return
      }

      // Create checkout session via the active provider.
      const currentUrl = window.location.href
      const session = await createCheckoutSession({
        planId,
        billingInterval: billingInterval as 'monthly' | 'annual',
        billingEmail: email,
        successUrl: `${currentUrl}?success=true`,
        cancelUrl: currentUrl,
      })

      const result = await billing.client.openCheckout({
        // Backend response carries either { url, sessionId } (Stripe) or
        // { transactionId } (Paddle). Provider client picks the right one.
        sessionIdOrUrl:
          (session as { url?: string | null }).url ??
          (session as { sessionId?: string | null }).sessionId ??
          null,
        transactionId:
          (session as { transactionId?: string | null }).transactionId ?? null,
        successUrl: `${currentUrl}?success=true`,
      })

      // Stripe path never reaches here (window has redirected). For Paddle
      // the checkout completes inline, so we refetch the API until the new
      // plan settles (the webhook updates the backend asynchronously),
      // showing the finalizing overlay for the duration.
      if (
        billing.providerName === 'paddle' &&
        result.status === 'completed'
      ) {
        setIsFinalizing(true)
        await refetchUntilSettled()
        void refreshTrialEligibility()
        toast.success(t('subscription.toast.upgradeSuccess'))
      }
    } catch (error: unknown) {
      // Backend may surface a plan_change_blocked_* response. Show the most
      // specific message we can; fall back to a generic toast otherwise.
      const data = (error as { response?: { data?: unknown } } | null)?.response
        ?.data as PlanChangePreflightResponse | undefined
      if (data && data.blocked && Array.isArray(data.violations)) {
        setBlockedModal({
          open: true,
          targetPlan: selectedPlan.code as PlanCode,
          preflight: data,
        })
      } else {
        toast.error(errorMessage(error, t('subscription.toast.checkoutFailed')))
      }
      logger.logError(
        error as Error,
        LogCategory.SUBSCRIPTION_CHECKOUT,
        {
          component: 'SubscriptionSettingsPage',
          planId,
          billingInterval,
        }
      )
    } finally {
      isSelectingPlanRef.current = false
      setIsLoading(false)
      setIsFinalizing(false)
    }
  }

  const handleCancelSubscription = async () => {
    setIsLoading(true)
    try {
      await cancelSubscription({
        cancelAtPeriodEnd: true,
        reason: 'User requested cancellation',
      })
      toast.success(t('subscription.toast.cancelScheduled'))
      void refetchSubscription()
      void refreshTrialEligibility()
    } catch (error: unknown) {
      toast.error(errorMessage(error, t('subscription.toast.cancelFailed')))
    } finally {
      setIsLoading(false)
    }
  }

  const handleReactivateSubscription = async () => {
    setIsLoading(true)
    try {
      await reactivateSubscription()
      toast.success(t('subscription.toast.reactivated'))
      void refetchSubscription()
      void refreshTrialEligibility()
    } catch (error: unknown) {
      toast.error(errorMessage(error, t('subscription.toast.reactivateFailed')))
    } finally {
      setIsLoading(false)
    }
  }

  const handleManageBilling = async () => {
    setIsLoading(true)
    try {
      // Create provider billing portal session (Stripe portal URL or Paddle
      // customer portal overview URL — backend resolves via the row's
      // BillingProvider).
      const portalSession = await createBillingPortalSession()

      if (billing.client) {
        billing.client.openPortal(portalSession.url)
      } else {
        // Fallback if the billing context hasn't loaded yet.
        window.location.href = portalSession.url
      }
    } catch (error: unknown) {
      toast.error(errorMessage(error, t('subscription.toast.portalFailed')))
      logger.logError(
        error as Error,
        LogCategory.SUBSCRIPTION,
        {
          component: 'SubscriptionSettingsPage',
          action: 'open_billing_portal',
        }
      )
    } finally {
      setIsLoading(false)
    }
  }


  // Presentation-only derivations (design 15c). Everything shown comes from
  // the plan catalogue, the current subscription and the synced usage row.
  const paidPlans = purchasablePlans(plans)
  const freePlan = plans.find((p) => p.code === PLAN_CODES.FREE) ?? null
  // Prefer the REST plan: usePlans() returns public plans only, so a
  // workspace on a retired plan (legacy Team AI) would otherwise resolve to
  // null and be treated as unsubscribed.
  const currentPlanCode: PlanCode | null =
    plan?.code ?? plans.find((cp) => cp.id === subscription?.planId)?.code ?? null
  const onPaidPlan = isPaidPlanCode(currentPlanCode)
  const savePercent = maxAnnualSavingsPercent(paidPlans)
  const anyTrialEligible = paidPlans.some((p) =>
    isPaidPlanCode(p.code) ? (trialEligibility?.[p.code] ?? false) : false,
  )

  const summary = (() => {
    if (isSubscriptionLoading) return undefined
    if (subscription && plan) {
      if (isTrialing) {
        return t('subscription.summary.trialing', { plan: plan.name, count: trialDaysLeft })
      }
      return !onPaidPlan && anyTrialEligible
        ? t('subscription.summary.onPlanWithTrial', { plan: plan.name })
        : t('subscription.summary.onPlan', { plan: plan.name })
    }
    return undefined
  })()

  return (
    <SettingsPage title={t('subscription.pageTitle')} description={summary}>
      {/* Post-checkout finalizing overlay — blocks interaction and stays
          centered/visible while the provider webhook settles the new plan,
          so the user can't fire off another action and always sees progress. */}
      {isFinalizing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm"
          role="status"
          aria-live="polite"
        >
          <div className="mx-4 flex max-w-sm flex-col items-center gap-3 rounded-xl border bg-card p-8 text-center shadow-xl">
            <Loader2 className="size-8 animate-spin text-primary" />
            <p className="font-semibold">
              {t('subscription.finalizing.title')}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('subscription.finalizing.description')}
            </p>
          </div>
        </div>
      )}

      {/* Current-subscription load state. Billing is server-authoritative
          and online-only, so the current subscription comes from the API
          (not the offline RxDB cache). While it's pending on first load we
          show a spinner; if it errors (e.g. offline) we show an inline
          retry. The plan-catalog cards below still render from RxDB. */}
      {FEATURE_FLAGS.ENABLE_SUBSCRIPTIONS &&
        isSubscriptionLoading &&
        !currentSubscription && (
          <div
            className="flex items-center gap-3 rounded-[14px] border bg-card p-[18px] text-sm text-muted-foreground"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="size-4 animate-spin text-primary" />
            {t('subscription.loading')}
          </div>
        )}

      {FEATURE_FLAGS.ENABLE_SUBSCRIPTIONS &&
        isSubscriptionError &&
        !currentSubscription && (
          <div className="space-y-3 rounded-[14px] border border-destructive/30 bg-destructive/5 p-[18px]">
            <div className="flex items-start gap-3">
              <AlertTriangle className="size-5 shrink-0 text-destructive" />
              <div className="space-y-1">
                <p className="font-semibold">
                  {t('subscription.loadError.title')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('subscription.loadError.description')}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetchSubscription()}
              disabled={isSubscriptionLoading}
            >
              {t('subscription.loadError.retry')}
            </Button>
          </div>
        )}

      {/* No Subscription - Start Trial */}
      {!isSubscriptionLoading &&
        !isSubscriptionError &&
        !subscription &&
        FEATURE_FLAGS.ENABLE_SUBSCRIPTIONS && (
          <div className="flex flex-col items-center gap-4 rounded-[14px] border bg-card p-8 text-center">
            <div className="inline-flex items-center justify-center rounded-full bg-brand-tint p-3 text-brand-tint-foreground">
              <Sparkles className="size-6" />
            </div>
            <div className="space-y-1.5">
              <h2 className="text-lg font-semibold">
                {t('subscription.trialCta.heading')}
              </h2>
              <p className="mx-auto max-w-md text-sm text-muted-foreground">
                {t('subscription.trialCta.body')}
              </p>
            </div>
            <Button
              onClick={handleStartTrial}
              disabled={isLoading || isApiDown}
              title={offlineTitle}
              size="lg"
              className="min-w-48"
            >
              <Sparkles className="size-4" />
              {isLoading ? t('subscription.trialCta.starting') : t('subscription.trialCta.start')}
            </Button>
            <p className="text-xs text-muted-foreground">
              {t('subscription.trialCta.footnote')}
            </p>
          </div>
        )}

      {/* Current plan + usage */}
      {subscription && plan && FEATURE_FLAGS.ENABLE_SUBSCRIPTIONS && (
        <CurrentPlanCard
          plan={plan}
          subscription={subscription}
          usage={usage}
          plans={plans}
          isTrialing={isTrialing}
          trialDaysLeft={trialDaysLeft}
          isActive={isActive}
          isLoading={isLoading}
          isApiDown={isApiDown}
          offlineTitle={offlineTitle}
          onManageBilling={handleManageBilling}
          onCancel={handleCancelSubscription}
          onReactivate={handleReactivateSubscription}
        />
      )}

      {/* Full usage breakdown (incl. guests/workspaces) — still behind its flag */}
      {FEATURE_FLAGS.SHOW_USAGE_METRICS && (
        <SettingsSection title={t('subscription.usage.detailsTitle')}>
          <div className="rounded-[14px] border bg-card p-[18px]">
            <UsageMetrics variant="inline" showTitle={false} showUpgrade />
          </div>
        </SettingsSection>
      )}

      {/* Plans */}
      {FEATURE_FLAGS.ENABLE_SUBSCRIPTIONS && paidPlans.length > 0 && (
        <SettingsSection
          title={onPaidPlan ? t('subscription.plans.change') : t('subscription.plans.upgrade')}
          aside={
            <BillingIntervalToggle
              value={billingInterval}
              onChange={setBillingInterval}
              savePercent={savePercent}
            />
          }
        >
          <div className="grid grid-cols-1 gap-3 pt-1 sm:grid-cols-2">
            {paidPlans.map((p) => (
              <UpgradePlanCard
                key={p.id}
                plan={p}
                isCurrentPlan={p.id === subscription?.planId}
                currentPlanCode={currentPlanCode}
                trialEligibility={trialEligibility}
                billingInterval={billingInterval}
                actionInProgress={isLoading}
                onSelect={handleSelectPlan}
              />
            ))}
          </div>

          {freePlan && (
            <p className="px-0.5 pt-2 text-[13px] text-muted-foreground">
              {t('subscription.freeIncludes', {
                members: freePlan.limits.members,
                tasks: freePlan.limits.tasks,
                projects: freePlan.limits.projects,
                storage: formatCompactBytes(freePlan.limits.storage),
                file: formatCompactBytes(freePlan.limits.maxFileSizeBytes),
              })}
              {/* Moving back to Free cancels at period end — same flow as the old Free card. */}
              {onPaidPlan && freePlan.id !== subscription?.planId && (
                <>
                  {' '}
                  <button
                    type="button"
                    onClick={() => void handleSelectPlan(freePlan.id, billingInterval)}
                    disabled={isLoading}
                    className="font-medium text-foreground underline underline-offset-4 disabled:opacity-50"
                  >
                    {t('subscription.planCard.cta.downgradeFree')}
                  </button>
                </>
              )}
            </p>
          )}
        </SettingsSection>
      )}

      <PlanChangeBlockedModal
        open={blockedModal.open}
        onOpenChange={(open) =>
          setBlockedModal((prev) => ({ ...prev, open }))
        }
        targetPlan={blockedModal.targetPlan}
        preflight={blockedModal.preflight}
        // Phase 0 deep-links left as no-ops; Phase 4 polish can wire these
        // to the members page and workspace settings page once the routes
        // are confirmed in this codebase.
      />
    </SettingsPage>
  )
}

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/settings/subscription',
)({
  component: SubscriptionSettingsPageWrapped,
})
